// ตารางสอนของฉัน (ภาคศาสนา) — ครูผู้สอนเปิดดูตารางของตัวเอง
//
// ตัวตารางกับเอกสารที่พิมพ์ใช้ของเดียวกับหน้าตารางสอนครูรายคนของฝ่ายการสอน
// (./teacherTimetableView) ต่างกันแค่ t_id มาจาก JWT แทนที่จะมาจาก dropdown
import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Badge } from "react-bootstrap";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import { buildTeacherGrid, shortTime, teacherLabel, exportTimetablePdf, printTimetableDoc } from "../utils/islamicTimetable";
import { TeacherTimetableGrid, teacherPrintDoc } from "./teacherTimetableView";

const API = process.env.REACT_APP_API;

export default function IslamicMyTimetable() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === "th" ? th : jawi;
  const tt = t.teacherTimetable;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [rows, setRows] = useState([]);
  // ชื่อมาจาก API ไม่ใช่ sessionStorage — user ที่เก็บตอนล็อกอินไม่มีชื่ออาหรับ
  const [me, setMe] = useState({});

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`)
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: shortTime(p.period_begin),
        period_end: shortTime(p.period_end),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${API}/islamic-teaching/myTimetable/${yearId}`)
      .then((res) => { setRows(res.data.timetable || []); setMe(res.data.teacher || {}); })
      .catch(() => { setRows([]); setMe({}); })
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  const grid = useMemo(() => buildTeacherGrid(rows), [rows]);
  const hasData = rows.length > 0;
  const hasConflict = rows.some((r) => r.has_teacher_conflict);

  const myDisplay = teacherLabel(lang, me);
  const yearLabel = yearList.find((y) => String(y.year_id) === String(yearId))?.year_year || "";

  const printDoc = () => teacherPrintDoc({ periods, grid, teacher: me, yearLabel, lang, t });

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">{tt.myPageTitle}</h4>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={(e) => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map((y) => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex align-items-center flex-wrap gap-2">
          <span className={`fw-semibold ${myDisplay.arabic ? "arabic-text" : ""}`}>{myDisplay.name}</span>
          {hasData && (
            <>
              <Badge bg="light" text="dark">{tt.slotSummary(rows.length)}</Badge>
              {hasConflict && <Badge bg="danger">{tt.conflictBadge}</Badge>}
            </>
          )}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="outline-danger"
              size="sm"
              disabled={!hasData || periods.length === 0}
              onClick={() => exportTimetablePdf(printDoc())}
            >
              <i className="ri-download-2-line me-1"></i>{tt.exportPdf}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!hasData || periods.length === 0}
              onClick={() => printTimetableDoc(printDoc())}
            >
              <i className="ri-printer-line me-1"></i>{tt.print}
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-2" style={{ overflowX: "auto" }}>
          {periods.length === 0 ? (
            <div className="text-center text-muted py-5">{tt.noPeriods}</div>
          ) : !hasData ? (
            <div className="text-center text-muted py-5">
              <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
              {tt.noMyTimetable}
            </div>
          ) : (
            <>
              <TeacherTimetableGrid periods={periods} grid={grid} dayNames={t.days} labels={tt} />
              {hasConflict && (
                <div className="text-danger mt-2" style={{ fontSize: "0.78rem" }}>
                  <i className="ri-alert-line me-1"></i>{tt.conflictHint}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
